/**
 * Client for the issue-fix console (the /app-issues/:id/console routes).
 *
 * It talks to the same support-service backend as issuesApi.ts, and the
 * stage/host/auth resolution below is a DELIBERATE COPY of that file's, not a
 * shared import: issuesApi.ts keeps those helpers module-private, and widening
 * its exported surface to serve this file would change a module every support
 * screen already depends on. The copy is the smaller risk of the two, but it
 * is a copy -- if the API host rules change, both files change together.
 *
 * Deliberately a flat object of thin calls: later boards (the owner approval
 * queue and the diff review) add `listAwaitingApproval` and `getDiff` here,
 * which is an addition to this object, not a restructure of it.
 */

const detectStage = (): 'prod' | 'dev' | 'staging' => {
  if (typeof window === 'undefined') return 'dev';
  try {
    const v = (import.meta as any)?.env?.VITE_STAGE;
    if (v === 'prod' || v === 'staging' || v === 'dev') return v;
  } catch {
    // ignore
  }
  const h = window.location.hostname;
  if (h.startsWith('admin.dev.') || h.includes('localhost') || h.startsWith('127.')) return 'dev';
  if (h.includes('stage')) return 'staging';
  return 'prod';
};

const baseDomain = (): string => {
  // DELIBERATE DIVERGENCE from issuesApi.ts, which still falls back to the
  // retired 'cafemenumaster.com' here. The branch is unreachable in a browser
  // (there is always a window), so this is the honest default for the one
  // product that exists rather than a behaviour change.
  if (typeof window === 'undefined') return 'rubytill.com';
  const h = window.location.hostname;
  return h.replace(/^(dev|stage|www|admin\.dev|admin\.stage|admin)\./, '');
};

const consoleApiHost = (): string => {
  try {
    const fromEnv = (import.meta as any)?.env?.VITE_ISSUES_API_BASE;
    if (fromEnv) return fromEnv;
  } catch {
    // ignore
  }
  // On localhost the Vite proxy (`/issues-api` -> dev backend) stands in, so we
  // never try to resolve `issues.api.dev.localhost`.
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  if (h === 'localhost' || h === '127.0.0.1') return '/issues-api';

  const stage = detectStage();
  const apex = baseDomain();
  if (stage === 'prod') return `https://issues.api.${apex}`;
  if (stage === 'staging') return `https://issues.api.stage.${apex}`;
  return `https://issues.api.dev.${apex}`;
};

function authHeader(): Record<string, string> {
  const token =
    (typeof localStorage !== 'undefined' && localStorage.getItem('jwtToken')) || '';
  return token ? { Authorization: token } : {};
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${consoleApiHost()}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...authHeader(),
      ...(init.headers || {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    /*
      A SERVICE MESSAGE IS RENDERED WORD FOR WORD, so it is thrown word for
      word. `planRollback` refuses a rollback with the one sentence that says
      WHY this particular change cannot be undone by redeploying old code --
      which table it created, which field rows already carry. Wrapping that in
      "console API 409: {json}" would put the only useful sentence on the
      screen behind punctuation the owner has to read past. The status still
      travels, on the error object, for anything that needs to branch on it.
    */
    let message = '';
    let parsed: unknown;
    try {
      const body = JSON.parse(text);
      parsed = body;
      if (body && typeof body.message === 'string') message = body.message;
      else if (body && typeof body.error === 'string') message = body.error;
    } catch {
      // Not JSON -- fall through to the status line below.
    }
    const err = new Error(message || `console API ${res.status}: ${text || res.statusText}`);
    (err as Error & { status?: number }).status = res.status;
    // The parsed body rides along too: a lock refusal carries the live `lock`.
    (err as Error & { body?: unknown }).body = parsed;
    throw err;
  }
  return res.json() as Promise<T>;
}

/** Where an issue sits in the fix loop. Mirrors AppIssue.status on the service. */
export type ConsoleIssueStatus =
  | 'new'
  | 'in_progress'
  | 'deploying'
  | 'awaiting_retest'
  | 'awaiting_owner_approval'
  | 'resolved'
  | 'rejected';

/** One line of Ruby's transcript for the current attempt. */
export interface ConsoleRunEvent {
  /** `tool` is a dimmed bullet, `say` is prose, `diff` is a fenced patch, `note` is a session rule. */
  kind: 'tool' | 'say' | 'diff' | 'note' | 'tester';
  text: string;
  /** Present on `diff` events -- the file the patch applies to. */
  file?: string;
  /** Present on `diff` events -- the patch lines, each already prefixed + or -. */
  lines?: string[];
  at?: string;
}

/** The cost rail. `model` stays the real model id even though the persona is Ruby. */
export interface ConsoleRunCost {
  elapsedSeconds?: number;
  model?: string;
  tokensIn?: number;
  tokensCached?: number;
  tokensOut?: number;
  costUsd?: number;
  /** Per-attempt costs for this issue, oldest first. */
  attempts?: Array<{ attempt: number; costUsd: number }>;
  totalUsd?: number;
  billedTo?: {
    category?: string;
    feature?: string;
    operation?: string;
    actor?: string;
  };
}

/**
 * THE STAGES A CONSOLE ROW CAN COME FROM.
 *
 * The service merges dev and prod rows into one list and ids are NOT unique
 * across stages, so every console row carries `key: "${sourceStage}:${id}"`.
 * This list is what `parseRowKey` recognises as a stage prefix — an id that
 * happens to contain a colon is left whole rather than split at it.
 */
export const CONSOLE_STAGES = ['dev', 'staging', 'prod'] as const;

/**
 * The identity of one console row: its stage and its id, from the service's
 * `key`. A key the service did not send (an older row, an older service) is
 * read as a bare id with an UNKNOWN stage — never as `dev`, because guessing
 * `dev` for a prod row is the exact confusion `key` exists to prevent.
 */
export function parseRowKey(raw: string): { sourceStage: string | null; id: string } {
  const i = raw.indexOf(':');
  if (i > 0) {
    const head = raw.slice(0, i);
    if ((CONSOLE_STAGES as readonly string[]).includes(head)) {
      return { sourceStage: head, id: raw.slice(i + 1) };
    }
  }
  return { sourceStage: null, id: raw };
}

/**
 * The React key, the selection identity and the link segment for one console
 * row — the SERVICE's `key` whenever it sent one.
 *
 * `environment` is the tester queue's name for the same thing `sourceStage` is
 * on the approval queue, so both row shapes resolve here.
 */
export function rowKeyOf(row: {
  key?: string;
  id: string;
  sourceStage?: string | null;
  environment?: string | null;
}): string {
  if (row.key) return row.key;
  const stage = row.sourceStage || row.environment;
  return stage ? `${stage}:${row.id}` : row.id;
}

/** Who has an issue checked out. Mirrors `AppIssue.lock` in shared-types. */
export interface IssueLock {
  userId: string;
  name?: string | null;
  email?: string | null;
  acquiredAt: number;
  expiresAt: number;
  takenOverFrom?: string | null;
}

/** The lock, if it is held and has not expired. */
export const liveLock = (lock: IssueLock | null | undefined, now = Date.now()): IssueLock | null =>
  lock && typeof lock.expiresAt === 'number' && lock.expiresAt > now ? lock : null;

export const lockHolderName = (l: IssueLock): string => l.name || l.email || 'Someone';

/** The holder a 409 lock refusal named, if that is what this error is. */
export const lockFromError = (e: unknown): IssueLock | null => {
  const b = (e as { status?: number; body?: { lock?: IssueLock } } | null);
  return b?.status === 409 && b.body?.lock ? b.body.lock : null;
};

/** The signed-in user's Cognito `sub`, off the JWT -- what `lock.userId` is compared to. */
export const currentUserId = (): string | null => {
  try {
    const t = typeof localStorage !== 'undefined' ? localStorage.getItem('jwtToken') : null;
    if (!t) return null;
    const claims = JSON.parse(atob((t.split('.')[1] || '').replace(/-/g, '+').replace(/_/g, '/')));
    return claims.sub || claims['cognito:username'] || null;
  } catch {
    return null;
  }
};

/* ------------------------------------------------------ the developer tools */

/** What a fix run may touch. Mirrors support-service lib/workScope.ts. */
export type WorkScope = 'ui' | 'backend' | 'infra';
/** What a developer may start without approval: the same three levels as a run's scope. */
export type DevAccess = WorkScope;

/** ui < backend < infra. `covers(a, b)`: level/scope `a` includes `b`. */
export const SCOPE_RANK: Record<WorkScope, number> = { ui: 0, backend: 1, infra: 2 };
export const covers = (a: WorkScope | null | undefined, b: WorkScope): boolean =>
  !!a && SCOPE_RANK[a] >= SCOPE_RANK[b];

export interface ScopeRequest {
  scope: WorkScope;
  reason: string;
  by: string | null;
  byName?: string | null;
  at: number;
  state: 'pending' | 'approved' | 'denied';
  decidedBy?: string | null;
  decidedAt?: number;
  decisionNote?: string;
}

/** Ruby's one-time analysis of an issue, saved on its row. */
export interface IssueTriage {
  summary: string;
  scope: 'ui' | 'backend' | 'infra';
  confidence?: 'high' | 'medium' | 'low';
  areas?: Array<{ repo: string; area: string; scope?: 'ui' | 'backend' | 'infra' }>;
  analysedAt?: number;
  analysedBy?: string | null;
  model?: string;
}

/** What the Overview tab shows about where and how the issue happened. */
export interface IssueOverviewFacts {
  page?: string;
  where?: string;
  device?: string;
  appVersion?: string;
  errors: number;
  failedRequests: Array<{ method: string; url: string; status: number }>;
  seen?: number;
  hasScreenshot: boolean;
}

/** One entry of the append-only history on the issue row (lib/timeline.ts). */
export interface TimelineEntry {
  at: number;
  kind: 'dispatch' | 'run' | 'confirm' | 'reject' | 'message' | 'approval-request' | 'approval-decision' | 'run-reset' | 'scope-violation'
    | 'checkout' | 'takeover' | 'release' | 'note';
  by?: string | null;
  byName?: string | null;
  attempt?: number;
  scope?: WorkScope;
  text?: string;
  previewUrl?: string;
  branch?: string;
  files?: number;
  impact?: WorkScope;
  costUsd?: number;
  durationMs?: number;
  failed?: boolean;
  state?: 'pending' | 'approved' | 'denied';
  delivered?: 'dispatched' | 'queued';
}

export interface HealthCheck {
  key: string;
  label: string;
  state: 'ok' | 'missing' | 'warn' | 'unknown';
  detail: string;
  fix?: string;
  blocking: boolean;
}

export interface ConsoleHealth {
  stage: string;
  ready: boolean;
  checks: HealthCheck[];
  checkedAt: number;
}

export interface DevAccessEntry {
  key: string;
  userId?: string | null;
  email: string;
  access: DevAccess;
  name?: string;
  canApprove?: boolean;
  /** Repo allow-list; absent = every repo on the allowlist. */
  repos?: string[];
  updatedBy?: string | null;
  updatedAt?: number;
}

/** The Controls access list (GET /admin/issues/console/access). */
export interface AccessControls {
  entries: DevAccessEntry[];
  enforcing: boolean;
  approvers: number;
  repos: string[];
  you: { userId: string; email: string | null; name: string | null };
}

/** One person in the platform-admin user pool. */
export interface PoolUser {
  userId: string;
  email: string;
  name: string | null;
  status: string | null;
  enabled: boolean;
  createdAt: number | null;
}

/** An attempt the service refused because the agent changed files past its scope. */
export interface ScopeViolation {
  attempt: number;
  allowed: WorkScope;
  impact: WorkScope;
  files: string[];
  at: number;
}

/* ------------------------------------------------------ the issue pipeline */
export type PipelineState = 'done' | 'active' | 'pending' | 'failed' | 'skip';
export interface PipelineStep { key: string; label: string; state: PipelineState; detail?: string; at?: number; url?: string; linkText?: string }
export interface PipelineAttempt {
  attempt: number; by?: string | null; profile?: string | null; model?: string | null; effort?: string | null; startedAt?: number;
  verdict: 'fixed' | 'still-broken' | 'blocked' | 'running' | 'waiting';
  verdictBy?: string | null; verdictText?: string | null;
  handoff?: { by?: string | null; text: string } | null;
  steps: PipelineStep[];
}
export interface PipelineLane { key: string; name: string; meta?: string; steps: PipelineStep[] }
/** support-service lib/pipeline.ts — every step of one issue, branch to prod. */
export interface IssuePipeline {
  branch: string | null; branchUrl: string | null; repo: string | null; stage: string;
  attempts: PipelineAttempt[]; lanes: PipelineLane[];
  live: { runUrl: string; steps: PipelineStep[] } | null;
  totals: { attempts: number; costUsd: number; costEstimated: boolean; people: string[]; startedAt?: number; finishedAt?: number };
  githubError?: string;
}

/* ---------------------------------------------------- Developer AI profiles */
export type AiEffort = 'low' | 'medium' | 'high' | 'xhigh' | 'max';
export interface AiModelChoice { providerId: string; modelId: string; effort?: AiEffort | null }
export interface AiProfile {
  id: string;
  name: string;
  analysis: AiModelChoice;
  develop: AiModelChoice;
  forDevelopers: boolean;
}
export interface AiProfilesDoc { profiles: AiProfile[]; defaultId: string; updatedAt?: number; updatedBy?: string | null }

/**
 * The effort levels a Claude model takes -- mirror of support-service
 * lib/devAiProfiles.ts `effortsFor` (the server re-validates on save).
 */
export function effortsFor(providerId: string, modelId: string): AiEffort[] {
  if (providerId !== 'anthropic') return [];
  if (/^claude-haiku/.test(modelId)) return [];
  if (/^claude-(sonnet|opus)-4-6/.test(modelId)) return ['low', 'medium', 'high', 'max'];
  if (/^claude-(opus-5|sonnet-5|fable-5|mythos-5|opus-4-8|opus-4-7)/.test(modelId)) return ['low', 'medium', 'high', 'xhigh', 'max'];
  return [];
}
export const EFFORT_LABEL: Record<AiEffort, string> = { low: 'Low', medium: 'Medium', high: 'High', xhigh: 'Extra high', max: 'Max' };

export interface ConsoleTools {
  /** The Developer AI profiles this person may pick per attempt, and the default. */
  aiProfiles?: { defaultId: string; options: Array<Omit<AiProfile, 'forDevelopers'>> };
  you: {
    userId: string; email: string | null; name: string | null; access: DevAccess; canApprove: boolean;
    /** False until an approver exists in Controls. */
    enforcing?: boolean;
    repos?: string[] | null;
  };
  scopeViolation?: ScopeViolation | null;
  workScope: WorkScope;
  scopeRequest: ScopeRequest | null;
  changes: {
    available: boolean;
    scope: WorkScope;
    ui: string[];
    backend: string[];
    infra: string[];
    verdict: 'safe' | 'review' | 'hold' | null;
    moneyPath: string[];
    schema: string[];
    deletions: string[];
    totals: { files: number; added: number; removed: number };
    files: Array<{ path: string; status: 'A' | 'M' | 'D'; added: number; removed: number }>;
  };
  run: { inFlight: boolean; attempt: number | null; dispatchedAt: number | null; canReset: boolean; summary: string | null };
  timeline: TimelineEntry[];
  queuedMessages: number;
  links: { repo: string; workflowRuns: string; branch: string | null; compare: string | null } | null;
  health?: ConsoleHealth;
}

/** What `message` did with the text: started a run, or saved it for the next one. */
export interface MessageResult {
  delivered: 'dispatched' | 'queued';
  attempt?: number;
  scope?: WorkScope;
  reason?: string;
  tools: ConsoleTools;
}

/** True when a refusal says the caller needs a scope approval first. */
export const needsApproval = (e: unknown): boolean =>
  !!(e as { body?: { needsApproval?: boolean } } | null)?.body?.needsApproval;

/** One row of the tester's queue rail, and the subject of the console itself. */
export interface ConsoleIssue {
  /**
   * `${environment}:${id}`, as `console.toMyQueueRow` stamps it. Ids are NOT
   * unique across stages, so this — not `id` — is the React key and the
   * selection identity. Optional only because the console also renders rows
   * that never came from that endpoint (the issue detail's Console tab).
   */
  key?: string;
  id: string;
  /** `null` when the row carries no title — `toMyQueueRow` sends `str() ?? null`. */
  title: string | null;
  status: ConsoleIssueStatus;
  attempt?: number;
  /** The tester's private test copy. Absent until a build has published one. */
  previewUrl?: string;
  /** 'prod' or 'dev' -- which environment the report came from. */
  environment?: 'prod' | 'dev';
  /** Who has it checked out, as the issue row carries it (may be expired -- see `liveLock`). */
  lock?: IssueLock | null;
  /**
   * A PROD issue being worked in dev: the id of its dev work copy. Every
   * console action (lock, Ruby, verdicts, tools) goes to this id, never `id`.
   */
  workId?: string | null;
  /** A worked prod issue: prod's own status (`status` is the work's). */
  prodStatus?: string | null;
  /** 'dev' | 'prod' — which stage the row was read from. */
  sourceStage?: string | null;
  /** The issue's category (`device_auto` = Android device auto-ticket). */
  category?: string | null;
  /** Plain facts for the Overview tab -- never the reporter's email or user id. */
  overview?: IssueOverviewFacts;
  /** Ruby's saved analysis (support-service lib/triage.ts). Absent = not analysed yet. */
  triage?: IssueTriage | null;
  /** The reporter's priority, as the issue row carries it. Absent = not set. */
  priority?: string;
  /** Where the report came from ('POS Terminal', 'POS Web'...), via `deriveSource`. */
  sourceLabel?: string;
  /** The machine form of the same fact ('pos-terminal', 'web'...) -- what the rail filters on. */
  sourceKind?: string;
  /** The app route the report was filed from, e.g. `/pos/stock`. Search only. */
  route?: string;
  branch?: string;
  /** What the reporter originally wrote. */
  reportedBy?: string;
  reportedText?: string;
  /** Ruby's instructions for what to retest. */
  retestNote?: string;
  /**
   * Path of the repro seed script the fix branch carries, when it carries one.
   * ABSENT IS THE NORMAL CASE -- most issues reproduce on whatever is already
   * on dev, and the console only offers "Set up repro" when this is set.
   */
  seedScript?: string;
  filesChanged?: number;
  buildPercent?: number;
  /** What Ruby is doing right now, e.g. "Ruby is building your test copy". */
  busyLabel?: string;
  busyDetail?: string;
  events?: ConsoleRunEvent[];
  cost?: ConsoleRunCost;
  /**
   * When the issue was reported, as the service records it (epoch ms).
   *
   * THE RAIL SORTS ON THIS. It fetches one page per status and used to append
   * them in status order, which put all the `new` rows first and buried every
   * testable row below the fold -- the owner read a rail where every visible
   * row said QUEUED and reasonably concluded the filter was broken. Absent
   * rather than defaulted to 0: a row with no timestamp sorts last, which is
   * honest, where 0 would claim it was reported at the epoch.
   */
  createdAt?: number;
  /** The scope the issue last ran at (absent = never ran; treated as ui). */
  workScope?: WorkScope;
  /** The widest scope the last run's files reached, stamped by the fix-run callback. */
  impactScope?: WorkScope;
  /** A pending/decided request for backend or infra scope. */
  scopeRequest?: ScopeRequest | null;
  /** Set when the AI scope gate refused the last attempt. */
  scopeViolation?: ScopeViolation | null;
}

/**
 * One row of the owner's approval pile (board 4).
 *
 * THIS IS A MIRROR OF `ApprovalQueueRow` in
 * `services/shared/support-service/src/handlers/console.ts`, field for field
 * and name for name. It was previously written from the mockup instead, under
 * names the service has never sent (the short `tests{Pass,Fail}` spellings
 * that `ConsoleDiff` also carried until they were collapsed -- see there, `files`,
 * `added`, `removed`, `attemptsTotal`, `elapsedMinutes`) — so an issue with
 * three failing tests and eleven changed files drew NO test chip and NO file
 * chip, and the owner approved it believing no checks had run.
 *
 * The service sends `null` for a field whose producer has not run. `null` is
 * UNKNOWN and is drawn as unknown — never as `0`, and never as a silent
 * absence that reads as "nothing to report".
 */
export interface ConsoleApprovalRow {
  /**
   * `${sourceStage}:${id}`. Ids are NOT unique across stages and this queue
   * merges prod rows in, so this is the React key and the row identity.
   */
  key?: string;
  id: string;
  /** 'dev' | 'prod' — which stage this row was read from. */
  sourceStage?: string | null;
  title?: string | null;
  status?: string;
  priority?: string | null;
  /** Epoch ms. */
  updatedAt?: number | null;
  /** The tester who signed it off. */
  testedBy?: string | null;
  /** EPOCH MS, not an ISO string — `confirmedAt` as the service stores it. */
  testedAt?: number | null;
  /** Changed files on the branch. `null` = no file list was ever stamped. */
  fileCount?: number | null;
  testsPassed?: number | null;
  testsFailed?: number | null;
  /** Every attempt on this issue, summed. */
  totalCostUsd?: number | null;
  /**
   * Set by the service from `classifyChange().moneyPath`. The frontend NEVER
   * recomputes this — it renders the classification the backend returned.
   * `null` = no file list, so nobody knows. Drawn as unknown, not as a "no".
   */
  touchesMoneyPath?: boolean | null;
  previewUrl?: string | null;
}

/** A file in the branch's name-status diff. Mirrors `ChangedFile` on the service. */
export interface ConsoleDiffFile {
  path: string;
  status: 'A' | 'M' | 'D';
  added: number;
  removed: number;
  /** Unified-diff lines, each already prefixed with ' ', '+' or '-'. */
  lines?: string[];
  /** The `@@ -a,b +c,d @@` header, when the service sent one. */
  hunkHeader?: string;
}

/**
 * The output of `classifyChange` on the service
 * (`services/shared/support-service/src/lib/changeImpact.ts`).
 *
 * This is a MIRROR OF A SERVER RESULT, not a second implementation: the
 * console renders these fields and never derives a verdict of its own, so the
 * owner and the service can never disagree about what a change touches.
 */
export interface ConsoleChangeImpact {
  additiveOnly: boolean;
  modifiesExisting: string[];
  infrastructure: string[];
  stepFunctions: string[];
  queues: string[];
  schema: string[];
  moneyPath: string[];
  migrationRequired: boolean;
  verdict: 'safe' | 'review' | 'hold';
}

/** What board 5 renders. */
export interface ConsoleDiff {
  files: ConsoleDiffFile[];
  impact: ConsoleChangeImpact;
  /**
   * WHETHER A CHANGED-FILE LIST WAS EVER STAMPED ON THIS RUN.
   *
   * The service sends `false` when it was not, precisely so the console does not
   * paint a verdict over data nobody has: `classifyChange([])` returns
   * `verdict: 'safe'`, which is true of an empty list and meaningless as a
   * statement about a change.
   *
   * `undefined` is UNKNOWN, NOT AVAILABLE. An older row, or a response from a
   * service that predates the field, must not fall through to the confident
   * path -- so `ImpactPanel` requires an explicit `true`.
   */
  filesAvailable?: boolean;
  /**
   * THE SAME URL THE TESTER OPENED. Not a rebuild and not a fresh preview —
   * the owner approves the exact copy that was signed off.
   */
  previewUrl?: string;
  title?: string;
  branch?: string;
  testedBy?: string;
  /** The tester's own words from confirmFix. */
  signOffNote?: string;
  /*
    ONE SPELLING, ROW TO BROWSER (2026-09-26). This endpoint used the short
    `tests{Pass,Fail}` spellings while the approval queue used these longer
    ones for the same two numbers on the same service -- two names for one
    field, which is the trap I1 fell into. The service collapsed them onto the
    DynamoDB row's own field names, which are also the shared-types names, and
    BOTH endpoints now emit these.
  */
  testsPassed?: number;
  testsFailed?: number;
  typesClean?: boolean;
  protectedPathsOk?: boolean;
  devOnlyBundleVerified?: boolean;
  attempts?: number;
  totalCostUsd?: number;
  elapsedSeconds?: number;
}

/* ------------------------------------------------ the deploy gate and the run */

/**
 * A minted, single-use deploy token (Task 9). The CODE IS NEVER HERE -- it
 * reaches the owner's authenticator, not the browser. The token only names
 * which deploy a code will be checked against, so a code minted for issue A
 * cannot ship issue B.
 */
/*
 * NARROWED 2026-09-26 to what `mintTokenOn` actually returns.
 *
 * It previously also declared `issueTitle`, `repos`, `order`, `sentTo` and
 * `attemptsLeft`. None of them has ever been sent, so every one of them was
 * always undefined and the verify screen always drew its fallbacks — most
 * damagingly "sent to your authenticator app", a destination on a screen whose
 * whole job is to tell the owner where to look for the code. The service does
 * not say where the code went, so neither does the screen.
 */
export interface ConsoleDeployToken {
  tokenId: string;
  /** Epoch ms. The screen counts down to it and stops offering the deploy. */
  expiresAt: number;
}

/** One node of the pipeline rail. `skip` is a stage that never ran. */
export type ConsoleStageState = 'done' | 'active' | 'pending' | 'failed' | 'skip';

export interface ConsoleRunStage {
  key: string;
  label: string;
  state: ConsoleStageState;
  /** The small grey line under the label, e.g. "probed 200 OK". */
  detail?: string;
  durationSeconds?: number;
}

export interface ConsoleRunLogLine {
  /** Clock as the service rendered it, e.g. "16:43:18". */
  at?: string;
  text: string;
  level?: 'info' | 'ok' | 'warn' | 'error';
}

/**
 * A live view of one deploy (or one rollback). Everything past `runId` and
 * `status` is optional: the pipeline draws what the service knows and draws the
 * rest as pending, never as done.
 */
export interface ConsoleRunSnapshot {
  runId: string;
  issueId?: string;
  status: 'queued' | 'running' | 'shipped' | 'failed' | 'rolled_back';
  stages?: ConsoleRunStage[];
  log?: ConsoleRunLogLine[];
  /** True while the service is still tailing the workflow's log. */
  streaming?: boolean;
  elapsedSeconds?: number;
  /** `key` of the stage that failed, when one did. */
  failedStage?: string;
  /**
   * The REAL compiler/deploy error, line for line, as the workflow printed it.
   * Rendered verbatim -- a summary of a tsc error is not a tsc error.
   */
  failureLog?: string[];
  /** Ruby's own reading of the failure. Prose, shown beside the log. */
  rubyExplanation?: string;
  /** What the failure needs from the owner, when it needs something. */
  needsYou?: string;
  /** Spend on this run alone, to 4 decimals like every other cost. */
  costUsd?: number;
  actionsMinutes?: number;
  verifiedBy?: string;
  verifiedAt?: string;
  branch?: string;
  /** e.g. "deploy.yml · pos-orders-service · run 2841". */
  workflow?: string;
  /** ISO. The 24-hour rollback window is measured from this. */
  shippedAt?: string;
  /** The merge commit this run shipped -- what a rollback reverts. */
  shippedSha?: string;
  /** The commit production returns to on a rollback. */
  lastGoodSha?: string;
  repos?: string[];
}

/** What `runSeed` created, in words a non-technical tester can act on. */
export interface ConsoleSeedResult {
  /** One sentence: "a held sale with a taxed item on Till 2". */
  summary: string;
  /** The individual records, when the script reported them. */
  created?: string[];
  /** Where it ran. Always dev -- the script refuses anything else. */
  stage?: string;
}

/** How the ledger is grouped. Mirrors the artboard's four chips. */
export type ConsoleSpendGrouping = 'issue' | 'person' | 'model' | 'event';

export interface ConsoleSpendFilters {
  /** `YYYY-MM`. Absent means the current month, as the service reckons it. */
  month?: string;
  groupBy?: ConsoleSpendGrouping;
}

export interface ConsoleSpendRow {
  /** Stable row key. For the by-issue grouping this is the issue id. */
  key: string;
  issueId?: string;
  title?: string;
  actor?: string;
  model?: string;
  attempts?: number;
  /** The last attempt failed -- the row shows the count in red. */
  failed?: boolean;
  /** Nothing has been billed yet. Rendered as $0.0000, never as blank. */
  queued?: boolean;
  tokensIn?: number;
  tokensOut?: number;
  elapsedSeconds?: number;
  /** 4-decimal precision, exactly as `calculateCost()` returned it. */
  costUsd: number;
}

export interface ConsoleSpend {
  rows: ConsoleSpendRow[];
  month?: string;
  category?: string;
  feature?: string;
  totalUsd?: number;
  issues?: number;
  avgPerIssueUsd?: number;
  totalSeconds?: number;
  totalTokensIn?: number;
  totalTokensOut?: number;
  /** Rows beyond the page, for the footer's "38 more issues this month". */
  moreRows?: number;
  /** The model id stays the real one -- the persona is Ruby, the record is not. */
  byModel?: Array<{ model: string; costUsd: number }>;
  cacheSavedUsd?: number;
  failedRunsUsd?: number;
  capUsd?: number;
}

/*
 * NO `listMyQueue`, AND NO `GET /admin/issues/console/my-queue` BEHIND IT
 * (both removed 2026-09-26).
 *
 * `/app-issues` has been the queue all along. A second list endpoint of the
 * console's own was a second queue with different membership -- it filtered by
 * assignee and showed a tester 3 of their 45 live issues. The rail now reads
 * the queue page's own rows via `issuesApi.list` and adapts them with
 * `toConsoleIssue`; there is one list and two views of it.
 */
export const consoleApi = {
  /**
   * THE CONSOLE'S PRIMARY ACTION: ask Ruby to fix this issue.
   *
   * Moves the issue to `in_progress` and dispatches the fixer. `note` is the
   * tester's own context ("only on till 2 after a shift change") and is
   * OPTIONAL -- an issue with a good report needs nothing added, and sending
   * `{ note: "" }` would write an empty tester line into the transcript. So the
   * body is omitted entirely when there is nothing to say.
   *
   * IT REFUSES for two reasons the tester cannot otherwise see: a run is
   * already in flight, or the service cannot resolve which repo to run
   * against. Both arrive here as the service's own sentence (see `call`) and
   * are rendered word for word -- "Something went wrong" would hide the only
   * part a human can act on.
   */
  startFix: (id: string, note?: string, scope?: WorkScope, profileId?: string | null) => {
    const text = (note || '').trim();
    const body: Record<string, string> = {};
    if (text) body.note = text;
    if (scope) body.scope = scope;
    if (profileId) body.profileId = profileId;
    return call<{ issue?: ConsoleIssue; runId?: string; attempt?: number; scope?: WorkScope }>(
      `/admin/issues/${encodeURIComponent(id)}/console/fix`,
      Object.keys(body).length ? { method: 'POST', body: JSON.stringify(body) } : { method: 'POST' },
    );
  },

  /** Setup health, history, scope approval, what the last run touched. */
  getTools: (id: string) =>
    call<ConsoleTools>(`/admin/issues/${encodeURIComponent(id)}/console/tools`),

  /** The Pipeline tab: attempts from history + PRs/builds/deploys read live from GitHub. */
  getPipeline: (id: string) =>
    call<{ pipeline: IssuePipeline }>(`/admin/issues/${encodeURIComponent(id)}/console/tools?view=pipeline`),

  /**
   * Talk to Ruby. `send: true` starts a new attempt carrying the text when the
   * issue can run; while one is going it is queued for the next attempt.
   */
  message: (id: string, text: string, send: boolean, scope?: WorkScope, profileId?: string | null) =>
    call<MessageResult>(`/admin/issues/${encodeURIComponent(id)}/console/tools`, {
      method: 'POST', body: JSON.stringify({ action: 'message', text, send, ...(scope ? { scope } : {}), ...(profileId ? { profileId } : {}) }),
    }),

  requestApproval: (id: string, scope: 'backend' | 'infra', reason: string) =>
    call<{ tools: ConsoleTools }>(`/admin/issues/${encodeURIComponent(id)}/console/tools`, {
      method: 'POST', body: JSON.stringify({ action: 'request-approval', scope, reason }),
    }),

  decideApproval: (id: string, approve: boolean, note?: string) =>
    call<{ tools: ConsoleTools }>(`/admin/issues/${encodeURIComponent(id)}/console/tools`, {
      method: 'POST', body: JSON.stringify({ action: 'decide-approval', approve, ...(note ? { note } : {}) }),
    }),

  resetRun: (id: string) =>
    call<{ status: ConsoleIssueStatus; tools: ConsoleTools }>(`/admin/issues/${encodeURIComponent(id)}/console/tools`, {
      method: 'POST', body: JSON.stringify({ action: 'reset-run' }),
    }),

  /*
    THE CHECK-OUT LOCK (support-service lib/issueLock.ts). `lock` checks out or
    renews; `force` is Take over. A refusal throws with `.status === 409` and
    `.body.lock` naming the holder -- see `lockFromError`.
  */
  /*
    A PROD row with no work copy yet is checked out with `sourceStage: 'prod'`:
    the service creates its dev work copy and answers with `workId`, the id
    every further console action must use (support-service lib/workCopy.ts).
  */
  lock: (id: string, force = false, sourceStage?: string | null) =>
    call<{ lock: IssueLock; workId?: string }>(
      `/admin/issues/${encodeURIComponent(id)}/console/lock${sourceStage === 'prod' ? '?source=prod' : ''}`,
      force ? { method: 'POST', body: JSON.stringify({ force: true }) } : { method: 'POST' },
    ),

  /**
   * THE WORKBENCH (support-service lib/workbench.ts): a 12h per-issue token the
   * developer pastes into Claude Code as `/issue <workId> <token>`; the
   * rubytill-workbench plugin then clones only this issue's repos and holds
   * the lock with it. A prod row with no work copy yet passes `sourceStage:
   * 'prod'`, exactly like `lock`, and gets its `workId` back.
   */
  workbenchToken: (id: string, sourceStage?: string | null) =>
    call<{ token: string; workId: string; expiresAt: number; command: string; api: string }>(
      `/admin/issues/${encodeURIComponent(id)}/console/workbench-token${sourceStage === 'prod' ? '?source=prod' : ''}`,
      { method: 'POST' },
    ),

  /** Release; an optional HANDOFF note (what was done, what's left) goes on the history. */
  unlock: (id: string, note?: string) =>
    call<{ released: boolean }>(`/admin/issues/${encodeURIComponent(id)}/console/lock`, {
      method: 'DELETE', ...(note && note.trim() ? { body: JSON.stringify({ note: note.trim() }) } : {}),
    }),

  /**
   * A TEAM NOTE on the overview -- no check-out needed, never sent to the
   * merchant or to Ruby. On a prod issue with no work copy yet, pass
   * `sourceStage: 'prod'`: the service creates the copy and returns `workId`.
   */
  note: (id: string, text: string, sourceStage?: string | null) =>
    call<{ workId: string; tools: ConsoleTools }>(
      `/admin/issues/${encodeURIComponent(id)}/console/tools${sourceStage === 'prod' ? '?source=prod' : ''}`,
      { method: 'POST', body: JSON.stringify({ action: 'note', text }) },
    ),

  /**
   * RUBY'S ANALYSIS -- run once and saved on the issue (`force` = Re-analyse).
   * Required before anyone can check the issue out.
   */
  analyse: (id: string, sourceStage?: string | null, force = false) =>
    call<{ workId: string; triage: IssueTriage; cached: boolean }>(
      `/admin/issues/${encodeURIComponent(id)}/console/tools${sourceStage === 'prod' ? '?source=prod' : ''}`,
      { method: 'POST', body: JSON.stringify({ action: 'analyse', ...(force ? { force: true } : {}) }) },
    ),

  /** The reporter's screenshot. A prod issue's is presigned BY PROD (read-only). */
  getScreenshotUrl: (id: string, sourceStage?: string | null) =>
    call<{ url: string | null }>(
      `/admin/issues/${encodeURIComponent(id)}/screenshot-url${sourceStage === 'prod' ? '?source=prod' : ''}`,
    ),

  /**
   * Release while the tab is CLOSING. A normal fetch is cancelled when the page
   * unloads; `keepalive` lets it finish. (sendBeacon cannot carry the
   * Authorization header.) Best effort -- the lease expiring is the backstop.
   */
  unlockOnExit: (id: string) => {
    try {
      fetch(`${consoleApiHost()}/admin/issues/${encodeURIComponent(id)}/console/lock`, {
        method: 'DELETE', keepalive: true, headers: { ...authHeader() },
      }).catch(() => undefined);
    } catch { /* the lease expires on its own */ }
  },

  /** The tester's verdict: it is fixed. Moves the issue to awaiting_owner_approval. */
  confirmFix: (id: string, note: string) =>
    call<{ issue: ConsoleIssue }>(
      `/admin/issues/${encodeURIComponent(id)}/confirm-fix`,
      { method: 'POST', body: JSON.stringify({ note }) },
    ),

  /** The tester's verdict: still broken. Adds an attempt to the SAME branch. */
  rejectFix: (id: string, note: string) =>
    call<{ issue: ConsoleIssue }>(
      `/admin/issues/${encodeURIComponent(id)}/reject-fix`,
      { method: 'POST', body: JSON.stringify({ note }) },
    ),

  /**
   * The owner's pile: every issue a tester has signed off, newest first.
   *
   * This is the SAME SET as `issuesApi.list({ status: 'awaiting_owner_approval' })`
   * — it carries the run facts (tester, checks, cost, money-path flag) that the
   * generic issue row has no place for.
   */
  listAwaitingApproval: () =>
    call<{ issues: ConsoleApprovalRow[] }>(
      '/admin/issues/console/awaiting-approval',
    ).then((r) => r.issues ?? []),

  /**
   * The branch diff, the service's impact classification, and the tester's preview.
   *
   * `sourceStage` is the stage the row came from, carried down from the
   * queue's `key`. The service defaults to DEV and reads prod only when it is
   * told to — so omitting this is not "whichever", it is dev, which is why the
   * approval queue's links carry the stage rather than leaving it off.
   */
  getDiff: (id: string, sourceStage?: string | null) => {
    const q = sourceStage ? `?source=${encodeURIComponent(sourceStage)}` : '';
    return call<ConsoleDiff>(`/admin/issues/${encodeURIComponent(id)}/console/diff${q}`);
  },

  /**
   * Ask for a verification code. What comes back is only the token the code
   * will be checked against — `{ tokenId, expiresAt }` and nothing else.
   *
   * `repos` names what the deploy would ship, and is bound onto the token at
   * mint time so the later /deploy cannot swap it. IT IS SENT ONLY WHEN THE
   * CALLER ACTUALLY KNOWS IT: neither the approval queue nor the diff response
   * carries repo names, so the ship screen has none and the service falls back
   * to what the issue itself records. Sending a list derived from file paths
   * would bind a GUESS onto a production deploy token.
   */
  mintDeployToken: (id: string, opts: { repos?: string[] } = {}) => {
    const repos = (opts.repos || []).filter((r) => typeof r === 'string' && r !== '');
    return call<ConsoleDeployToken>(
      `/admin/issues/${encodeURIComponent(id)}/deploy-token`,
      repos.length
        ? { method: 'POST', body: JSON.stringify({ repos }) }
        : { method: 'POST' },
    );
  },

  /**
   * Ship it. The service re-checks the code against `tokenId` AND against this
   * issue, so a code minted for another issue is refused server-side too --
   * the six-digit gate on the screen is convenience, not the security boundary.
   */
  deploy: (id: string, tokenId: string, code: string) =>
    call<{ runId: string }>(
      `/admin/issues/${encodeURIComponent(id)}/deploy`,
      { method: 'POST', body: JSON.stringify({ tokenId, code }) },
    ),

  /**
   * One poll of a run's live state.
   *
   * `runId` is the id `deploy` or `rollback` returned. The pipeline route only
   * carries an issue id, so the console passes the issue id when it has no run
   * id and the service answers with that issue's latest run -- which is why
   * this takes a single opaque id rather than an issue id plus an attempt.
   */
  watchRun: (runId: string) =>
    call<ConsoleRunSnapshot>(`/admin/issues/console/runs/${encodeURIComponent(runId)}`),

  /**
   * Put production back to the last good build. A ROLLBACK IS A PRODUCTION
   * DEPLOY, so it needs its own fresh code -- the one from the ship is spent.
   *
   * It rejects with the service's own sentence when `planRollback` refuses;
   * callers render that message as it arrived.
   */
  rollback: (id: string, tokenId: string, code: string) =>
    call<{ runId: string }>(
      `/admin/issues/${encodeURIComponent(id)}/rollback`,
      { method: 'POST', body: JSON.stringify({ tokenId, code }) },
    ),

  /** The AI usage ledger for the issue-fix feature. */
  listSpend: (filters: ConsoleSpendFilters = {}) => {
    const qs = new URLSearchParams();
    if (filters.month) qs.set('month', filters.month);
    if (filters.groupBy) qs.set('groupBy', filters.groupBy);
    const q = qs.toString();
    return call<ConsoleSpend>(`/admin/issues/console/spend${q ? `?${q}` : ''}`);
  },

  /**
   * Run the branch's repro seed against DEV and say what it made. The script's
   * own first statement is `assertDevOnly`, which refuses an unset stage rather
   * than guessing -- so a refusal arrives here as a message, not as a silent no-op.
   */
  runSeed: (id: string) =>
    call<ConsoleSeedResult>(
      `/admin/issues/${encodeURIComponent(id)}/console/seed`,
      { method: 'POST' },
    ),
};

/**
 * SYSTEM → CONTROLS. Approvers only (the service refuses anyone else).
 */
export const controlsApi = {
  getAccess: () => call<AccessControls>('/admin/issues/console/access'),

  /** Developer AI profiles (owner / Controls approvers). Same route as access. */
  getAiProfiles: () => call<AiProfilesDoc & { builtIn: AiProfile }>('/admin/issues/console/access?section=ai-profiles'),
  saveAiProfiles: (doc: AiProfilesDoc) =>
    call<AiProfilesDoc>('/admin/issues/console/access', {
      method: 'POST', body: JSON.stringify({ section: 'ai-profiles', ...doc }),
    }),

  /** `access: null` removes the person (back to the UI-only default). */
  setAccess: (w: {
    userId?: string | null; email: string; name?: string | null;
    access: DevAccess | null; canApprove?: boolean; repos?: string[];
  }) =>
    call<{ entries: DevAccessEntry[]; enforcing: boolean; approvers: number }>('/admin/issues/console/access', {
      method: 'POST', body: JSON.stringify(w),
    }),

  /** Browse the platform-admin user pool; `q` matches the start of an email. */
  browseUsers: (q?: string, next?: string | null) => {
    const qs = new URLSearchParams();
    if (q) qs.set('q', q);
    if (next) qs.set('next', next);
    const t = qs.toString();
    return call<{ users: PoolUser[]; next: string | null }>(`/admin/issues/console/access/users${t ? `?${t}` : ''}`);
  },
};
